import {
  GetUserAppActions,
  GetUsers,
  ISimpleUser,
  Login,
  Logout,
  PermissionItem,
  UserInfo,
} from '@/apis/modules/user'
import { AppPermissions } from '@/config/app'
import { LOGININFOKEY, USERINFOKEY } from '@/config/constant'
import { hasPermission } from '@/router/utils'
import { resetStore } from '@/store/utils'
import { Storage, flatTreeData } from '@/utils/index'
import { defineStore } from 'pinia'
import wujieVue from 'wujie-vue3'

type KeyInP = keyof typeof AppPermissions

interface UserState {
  userInfo: UserInfo
  permissionItems: PermissionItem[]
  userPermission: Record<KeyInP, boolean>
  userItems: ISimpleUser[]
}
export const useUserStore = defineStore({
  id: 'user',
  state: (): UserState => {
    const userInfo = Storage.get(USERINFOKEY)
    return {
      userInfo: userInfo,
      permissionItems: [],
      userPermission: {} as Record<KeyInP, boolean>,
      userItems: [],
    }
  },
  getters: {
    token: (state) => state.userInfo?.xaToken || '',
  },
  actions: {
    /**
     * 获取登录账号信息
     */
    getLoginInfo(): string {
      return Storage.get(LOGININFOKEY)
    },

    /**
     * 缓存登录账号信息
     */
    setLoginInfo(value: string) {
      value ? Storage.set(LOGININFOKEY, value) : Storage.remove(LOGININFOKEY)
    },

    /**
     * 登录
     */
    async login(query: any) {
      const data = await Login({
        ...query,
        password: btoa(query.password),
      })
      this.pushEventTags('login', query.username)
      if (btoa) {
        const value = btoa(`${query.username.trim()},${query.password.trim()}`)
        this.setLoginInfo(value)
      }
      this.setUserInfo(data)
      return data
    },

    /**
     * 退出登录
     */
    logout() {
      return Logout().then(() => {
        this.setUserInfo(null)
        this.setLoginInfo(null)
        resetStore()
      })
    },

    /**
     * 设置更新用户信息
     */
    setUserInfo(data: UserInfo) {
      if ((this.userInfo?.xaToken || '') !== (data?.xaToken || '')) {
        // IAM 重挂载会保留 Router 和 Pinia，登录令牌变化时销毁旧实例。
        wujieVue.destroyApp('iam-tob')
      }
      this.userInfo = data || null
      if (data?.xaToken) {
        document.cookie = `xa_token=${data.xaToken};path=/`
      }
      saveUserInfo(data)
    },

    has(permKeys: string[]) {
      return hasPermission(permKeys, this.permissionItems)
    },

    /**
     * 获取用户权限菜单
     */
    async getUserActions() {
      const permissionTree = await GetUserAppActions()
      this.permissionItems = flatTreeData(permissionTree, 'childList')
      const userPermission = (Object.keys(AppPermissions) as KeyInP[]).reduce((total, key) => {
        total[key] = this.has(AppPermissions[key])
        return total
      }, {} as Record<KeyInP, boolean>)
      this.userPermission = userPermission
      // console.log(userPermission)
      return this.permissionItems
    },

    /**
     * 获取IAM当前租户的用户列表
     */
    async getUsers() {
      const userItems = await GetUsers().alert()
      this.userItems = userItems
      return userItems
    },

    /**
     * 事件tag统计
     */
    pushEventTags(category: string, action: string) {
      if (!window._hmt) return
      const eventTags = ['_trackEvent', category, action]
      const userName = this.userInfo?.realName || this.userInfo?.name
      if (userName) eventTags.push(userName)
      window._hmt.push(eventTags)
    },
  },
})

function saveUserInfo(data: UserInfo) {
  data ? Storage.set(USERINFOKEY, data) : Storage.remove(USERINFOKEY)
}

export function useUserPermission() {
  const userStore = useUserStore()
  return userStore.userPermission
}

export function useUserName() {
  const userStore = useUserStore()
  // console.log("xxxxxxx  ", userStore)
  return userStore.userInfo.name
}
